import { useCallback, useMemo } from 'react';
import { useViewedCompanyID, useViewedCompanyScope } from '@/hooks/useViewedCompanyID';
import { permissionsService, type PermissionKey } from '@/lib/services/permissions.service';

const PERMISSIONS_STALE_TIME = 5 * 60 * 1000;

export interface PermissionsState {
	permissions: ReadonlySet<string>;
	isBypass: boolean;
	isLoading: boolean;
	isError: boolean;
	hasPermission: (key: PermissionKey) => boolean;
	hasAnyPermission: (keys: readonly PermissionKey[]) => boolean;
}

// Cosmetic gating: fail-open while unresolved (loading/error/no company) so nothing flash-hides; the server enforces.
export const usePermissions = (): PermissionsState => {
	const companyId = useViewedCompanyID();
	const scope = useViewedCompanyScope();
	const { data, isError } = permissionsService.useMyEffectivePermissions(scope, {
		enabled: !!companyId,
		staleTime: PERMISSIONS_STALE_TIME,
	});

	const resolved = data !== undefined;
	const isBypass = data?.isBypass ?? false;
	const permissions = useMemo(() => new Set(data?.permissions ?? []), [data]);

	const hasPermission = useCallback(
		(key: PermissionKey) => !resolved || isBypass || permissions.has(key),
		[resolved, isBypass, permissions],
	);
	const hasAnyPermission = useCallback(
		(keys: readonly PermissionKey[]) => !resolved || isBypass || keys.some((key) => permissions.has(key)),
		[resolved, isBypass, permissions],
	);

	return useMemo(
		() => ({ permissions, isBypass, isLoading: !resolved, isError, hasPermission, hasAnyPermission }),
		[permissions, isBypass, resolved, isError, hasPermission, hasAnyPermission],
	);
};

export const useHasPermission = (key: PermissionKey): boolean => usePermissions().hasPermission(key);

export const useHasAnyPermission = (keys: readonly PermissionKey[]): boolean => usePermissions().hasAnyPermission(keys);
